'use client';

import clsx from 'clsx';
import { createContext, useContext, type ForwardedRef, type ReactNode } from 'react';
import {
  Collection as RACCollection,
  Tab as RACTab,
  TabList as RACTabList,
  TabPanel as RACTabPanel,
  Tabs as RACTabs,
  type TabsProps as RACTabsProps,
  type TabProps as RACTabProps,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import type {
  AriaLabelingProps,
  CollectionItem,
  CollectionProps,
  CommonProps,
  Key,
  StringLikeChildren,
} from '../types.js';
import { Text } from '../typography/text.js';

interface TabsContextValue {
  tabsProps?: AriaLabelingProps;
}

const TabsContext = createContext<TabsContextValue>({});

export interface TabsProps
  extends
    CommonProps,
    AriaLabelingProps,
    Pick<RACTabsProps, 'selectedKey' | 'defaultSelectedKey' | 'disabledKeys' | 'onSelectionChange'> {
  /** The children of the `<Tabs>` element. Should include `<TabList>` and `<TabPanels>` elements. */
  children: ReactNode;
}

function Tabs(
  {
    children,
    UNSAFE_className,
    UNSAFE_style,

    // Aria labeling props
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-details': ariaDetails,

    ...props
  }: TabsProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  useProductionWarning(() => {
    if (!ariaLabel && !ariaLabelledBy) {
      console.warn('Tabs requires one of aria-label / aria-labelledby for accessibility');
    }
  }, [ariaLabel, ariaLabelledBy]);

  return (
    <TabsContext.Provider
      value={{
        tabsProps: {
          'aria-label': ariaLabel,
          'aria-labelledby': ariaLabelledBy,
          'aria-describedby': ariaDescribedBy,
          'aria-details': ariaDetails,
        },
      }}
    >
      <RACTabs
        {...props}
        ref={ref}
        className={clsx('cim-tabs', UNSAFE_className)}
        style={UNSAFE_style}
        keyboardActivation="manual"
      >
        {children}
      </RACTabs>
    </TabsContext.Provider>
  );
}

/**
 * Organizes content into multiple sections that users can switch between.
 *
 * See [tabs usage guidelines](https://ui.cimpress.io/components/tabs/).
 */
const _Tabs = withStyleProps(forwardRef(Tabs), 'Tabs');

export { _Tabs as Tabs };

export interface TabListProps<T extends CollectionItem> extends CollectionProps<T> {}

/** Renders a collection of `Tab` components within `Tabs`. */
export function TabList<T extends CollectionItem>({ children, ...props }: TabListProps<T>) {
  const { tabsProps } = useContext(TabsContext);
  const {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-details': ariaDetails,
  } = tabsProps ?? {};

  return (
    <RACTabList
      {...props}
      className="cim-tab-list"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      aria-details={ariaDetails}
    >
      {children}
    </RACTabList>
  );
}

TabList.displayName = 'TabList';

export interface TabProps extends Pick<RACTabProps, 'isDisabled' | 'onHoverStart' | 'onHoverEnd'> {
  /** The unique ID of the tab. */
  id?: Key;
  /** The text to display as the tab title. */
  children: StringLikeChildren;
  /** An icon displayed before the tab title. */
  iconStart?: ReactNode;
  /** A badge displayed after the tab title. */
  badge?: ReactNode;
}

/** Renders a single tab within `TabList`. */
export function Tab({ children, iconStart, badge, ...props }: TabProps) {
  return (
    <RACTab {...props} className="cim-tab-item">
      {iconStart}
      <Text as="span" variant="medium-semibold" alignment="center">
        {children}
      </Text>
      {badge}
    </RACTab>
  );
}

Tab.displayName = 'Tab';

export interface TabPanelsProps<T extends CollectionItem> extends CollectionProps<T> {}

/** Renders a collection of `TabPanel` components within `Tabs`. */
export function TabPanels<T extends CollectionItem>({ ...props }: TabPanelsProps<T>) {
  return <RACCollection {...props} />;
}

TabPanels.displayName = 'TabPanels';

export interface TabPanelProps {
  /** The content to display in the tab panel. */
  children: ReactNode;
  /** The unique id of the associated tab. */
  id?: Key;
}

/** Renders a single tab panel within `TabPanels`. */
export function TabPanel({ children, ...props }: TabPanelProps) {
  return (
    <RACTabPanel {...props} className="cim-tab-panel">
      {children}
    </RACTabPanel>
  );
}

TabPanel.displayName = 'TabPanel';
